import { FloatingPanels } from "orcs-design-system";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { isEmpty } from "lodash";
import {
  Z_INDEX,
  FULL_SCREEN_SPACING,
  PANEL_POSITIONING,
} from "src/components/NodeVisualizer/constants";
import { useOrgChartContext } from "src/components/OrgChart/context/OrgChartContext";
import { EventBus } from "src/services/eventEmitter";
import {
  addLocalStorageItem,
  getLocalStorageItem,
} from "src/services/localStorage";
import { ORG_CHART_DEFAULT_PANEL_ID } from "src/components/OrgChart/utils/storeInLocalStorage";
import { SOURCE_TYPES } from "src/components/AIContent/consts";
import { ENTITY_TYPES } from "src/consts/entityTypes";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import {
  useChangelogFeatureEnabled,
  useMetricsPanelEnabled,
} from "src/components/OrgChart/odDemo/featureFlags";
import { AISummaryWithAgentCore } from "../../../AIContent/AISummaryWithAgentCore";
import ChangelogView from "../ChangelogView";
import MetricsPanel from "./panels/MetricsPanel";
import ViewOptions from "./panels/ViewOptions";
import Properties from "./panels/Properties";
import Legend from "./panels/Legend";
import { PANEL_ID, ORG_CHART_PANEL_EVENTS } from "./const";
import { Details } from "./panels/Details/Details";

// Wrapper to position panels relative to viewport in full-screen mode
const PanelWrapper = styled.div`
  ${({ isFullScreen }) =>
    isFullScreen &&
    `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: ${Z_INDEX.PANELS}; /* Above canvas (z-index: ${Z_INDEX.CANVAS}) */
    
    > * {
      pointer-events: auto;
    }
  `}
`;

const LegendPanel = {
  id: PANEL_ID.legend,
  iconName: "question-circle",
  title: "Legend",
  content: <Legend />,
};

const ViewOptionsPanel = {
  id: PANEL_ID.viewOptions,
  iconName: "eye",
  title: "View Options",
  content: <ViewOptions />,
};

const MetricsPanelComponent = {
  id: PANEL_ID.metrics,
  iconName: "chart-line",
  title: "Team Insights",
  content: <MetricsPanel />,
};

const PropertiesPanel = {
  id: PANEL_ID.properties,
  iconName: "tag",
  title: "Properties",
  content: <Properties />,
};

const DetailsPanel = {
  id: PANEL_ID.details,
  iconName: "info-circle",
  title: "Details",
  content: <Details />,
};

const AISummaryPanel = () => {
  const { state } = useOrgChartContext();
  const [id, setId] = useState();
  const [isPanelVisible, setIsPanelVisible] = useState(false);

  useEffect(() => {
    const newId =
      state?.expandedPanel?.data?.selectedEntity || state.currentViewTeam?.id;

    if (newId) {
      setId(newId);
    }
  }, [state?.expandedPanel?.data?.selectedEntity, state.currentViewTeam?.id]);

  // Listen for panel selection changes
  useEffect(() => {
    const handlePanelChange = (panelId) => {
      setIsPanelVisible(panelId === PANEL_ID.ai);
    };

    EventBus.on(
      ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
      handlePanelChange
    );

    return () => {
      EventBus.off(
        ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
        handlePanelChange
      );
    };
  }, []);

  if (id) {
    return (
      <AISummaryWithAgentCore
        sourceId={SOURCE_TYPES.team_summary}
        subjectId={id}
        title=""
        isPanelVisible={isPanelVisible}
      />
    );
  }
  return undefined;
};

const OrgChartPanel = ({
  hideDetailsPanelInitially = false,
  isFullScreen = false,
  headerHeight = 0,
}) => {
  const { state } = useOrgChartContext();
  const flags = useWorkspaceFeatureFlags();
  const isChangelogEnabled = useChangelogFeatureEnabled();
  const isMetricsPanelEnabled = useMetricsPanelEnabled(state);
  const [forcePanelId, setForcePanelId] = useState(null);

  // Track if this is the initial mount - used to detect first render
  const isInitialMountRef = useRef(true);

  // Get panel ID from localStorage - if null/undefined, default to Details
  // This ensures Details panel opens for active team on initial load when no preference exists
  const initialPanelIdFromStorage =
    getLocalStorageItem(ORG_CHART_DEFAULT_PANEL_ID) ?? PANEL_ID.details;

  // Track the currently open panel - initialize from localStorage or default to Details
  // This state is the source of truth for which panel is currently open
  const [currentPanelId, setCurrentPanelId] = useState(
    initialPanelIdFromStorage
  );

  // Use a ref to track the stable panel ID to prevent unnecessary recalculations
  // This ref persists across entity changes, preventing FloatingPanels from updating
  const stablePanelIdRef = useRef(initialPanelIdFromStorage);

  // Force show details panel when entering full-screen mode
  useEffect(() => {
    if (isFullScreen && !hideDetailsPanelInitially) {
      // Set a key to force FloatingPanels to re-initialize with the new defaultPanelId
      setForcePanelId(PANEL_ID.details);
      // Also emit event to set the panel
      EventBus.emit(
        ORG_CHART_PANEL_EVENTS.SET_SELECTED_PANEL,
        PANEL_ID.details
      );
    } else if (!isFullScreen) {
      // Clear forced panel when exiting full-screen
      setForcePanelId(null);
    }
  }, [isFullScreen, hideDetailsPanelInitially]);

  // Track current panel - always preserve whatever panel is open
  // Use ref to track previous entity to detect actual changes
  const previousEntityRef = useRef(null);

  useEffect(() => {
    const handlePanelChange = (paneId) => {
      // Store selected panel in local storage
      addLocalStorageItem(ORG_CHART_DEFAULT_PANEL_ID, paneId);
      // Track current panel ID - whatever panel is open stays open
      setCurrentPanelId(paneId);
    };

    // Register event listeners
    EventBus.on(
      ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
      handlePanelChange
    );

    // On unmount, deregister event listeners
    return () => {
      EventBus.off(
        ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
        handlePanelChange
      );
    };
  }, []);

  /**
   * Panel State Management Logic
   *
   * This hook manages which panel is open when teams/people are selected in the org chart.
   * It ensures consistent, predictable behavior that is testable and repeatable.
   *
   * BEHAVIOR RULES:
   *
   * 1. INITIAL LOAD (first render with a selected entity):
   *    - If localStorage has a panel ID: use that panel
   *    - If localStorage is null/undefined: default to Details panel
   *    - This ensures Details panel opens for active team when no preference exists
   *
   * 2. TEAM/PERSON CHANGE (clicking a new team or person):
   *    - If a panel is currently open (currentPanelId is truthy): keep that panel open
   *    - If no panel is open (currentPanelId is null/undefined): open Details panel
   *    - This ensures users always see content when switching teams/people
   *
   * STATE MANAGEMENT:
   * - currentPanelId (state): Source of truth for which panel is currently open
   * - stablePanelIdRef (ref): Persists across renders to prevent unnecessary recalculations
   * - isInitialMountRef (ref): Tracks if this is the first render
   *
   * TESTING:
   * To test this behavior:
   * 1. Clear localStorage and load org chart → Details panel should open
   * 2. Open Metrics panel, then click different team → Metrics panel should stay open
   * 3. Close panel (set to null), then click different team → Details panel should open
   *
   * This behavior is always enabled for all users (not behind a feature flag).
   * The panel content will update automatically via its own state subscriptions.
   * Use useLayoutEffect to update ref synchronously before browser paint, preventing flash.
   */
  useLayoutEffect(() => {
    const selectedEntity =
      state?.expandedPanel?.data?.selectedEntity || state?.currentViewTeam?.id;
    const previousEntity = previousEntityRef.current;

    // Handle initial mount - use localStorage value if available, otherwise default to Details
    if (isInitialMountRef.current && selectedEntity) {
      isInitialMountRef.current = false;
      previousEntityRef.current = selectedEntity;

      // On initial load: use localStorage value if it exists, otherwise default to Details
      // This ensures Details panel opens for active team when no preference exists
      const initialPanelId = initialPanelIdFromStorage;

      stablePanelIdRef.current = initialPanelId;
      setCurrentPanelId(initialPanelId);
      addLocalStorageItem(ORG_CHART_DEFAULT_PANEL_ID, initialPanelId);
      EventBus.emit(ORG_CHART_PANEL_EVENTS.SET_SELECTED_PANEL, initialPanelId);
      return;
    }

    // Only act if entity actually changed (not on initial mount or same entity)
    if (selectedEntity && selectedEntity !== previousEntity) {
      // Update ref immediately to prevent multiple triggers
      previousEntityRef.current = selectedEntity;

      // Team/person changed - determine which panel to show
      // Logic:
      // 1. If a panel is currently open (currentPanelId is truthy), keep it open
      // 2. If no panel is open (currentPanelId is null/undefined), open Details panel
      // This ensures users always see content when switching teams/people
      const panelToKeep = currentPanelId ?? PANEL_ID.details;

      // CRITICAL: Update the stable ref SYNCHRONOUSLY before browser paint
      // This ensures defaultPanelId (which uses stablePanelIdRef.current) has the correct value
      // when FloatingPanels renders, preventing the flash
      stablePanelIdRef.current = panelToKeep;

      // Update state if panel changed (e.g., opened Details when none was open)
      if (panelToKeep !== currentPanelId) {
        setCurrentPanelId(panelToKeep);
      }

      // Update localStorage and emit event synchronously to prevent flash
      // This ensures the panel is set before any visual updates occur
      addLocalStorageItem(ORG_CHART_DEFAULT_PANEL_ID, panelToKeep);
      EventBus.emit(ORG_CHART_PANEL_EVENTS.SET_SELECTED_PANEL, panelToKeep);
    } else if (selectedEntity) {
      // Update ref even if we don't emit
      previousEntityRef.current = selectedEntity;
    }
  }, [
    state?.expandedPanel?.data?.selectedEntity,
    state?.currentViewTeam?.id,
    currentPanelId,
    initialPanelIdFromStorage,
  ]);

  // calc panels to display based on state
  const panelsToDisplay = useMemo(() => {
    const panels = [];

    // ai panel - only available for teams, not people
    const selectedEntityType = state?.expandedPanel?.data?.selectedEntityType;
    const isTeamSelected =
      !selectedEntityType || selectedEntityType === ENTITY_TYPES.GROUP;
    if (flags?.enableAISummaryOnTeamDetailsPage && isTeamSelected) {
      const AIPanel = {
        id: PANEL_ID.ai,
        iconName: "sparkles",
        title: "AI Summary",
        content: <AISummaryPanel />,
      };
      panels.push(AIPanel);
    }

    // legend panel (always show)
    panels.push(LegendPanel);

    // view options panel
    panels.push(ViewOptionsPanel);

    // metrics panel (gated by feature flag and team selection)
    if (isMetricsPanelEnabled) {
      panels.push(MetricsPanelComponent);
    }

    // only show properties panel if there are loaded teams or members
    if (!isEmpty(state?.teams) || !isEmpty(state?.members)) {
      panels.push(PropertiesPanel);
    }

    // legend panel (always show)
    panels.push(DetailsPanel);

    return panels;
  }, [state, flags?.enableAISummaryOnTeamDetailsPage, isMetricsPanelEnabled]);

  // Update stable ref when currentPanelId changes (user clicks a panel)
  // This ensures the ref always has the latest panel ID for defaultPanelId calculation
  useEffect(() => {
    if (currentPanelId) {
      stablePanelIdRef.current = currentPanelId;
    }
  }, [currentPanelId]);

  const defaultPanelId = useMemo(() => {
    // If forcing a panel ID (e.g., when entering full-screen), use that
    if (forcePanelId !== null) {
      return forcePanelId;
    }

    // If hiding details panel initially, don't show any panel
    if (hideDetailsPanelInitially) {
      return null;
    }

    // In full-screen mode, always show details panel by default
    if (isFullScreen) {
      return PANEL_ID.details;
    }

    // CRITICAL: Always prioritize currentPanelId (most recent panel state)
    // This is the source of truth for which panel is currently open
    // Fall back to stable ref, then Details panel as final fallback
    // This ensures consistent behavior: if no panel is open, default to Details
    return currentPanelId ?? stablePanelIdRef.current ?? PANEL_ID.details;
  }, [hideDetailsPanelInitially, isFullScreen, forcePanelId, currentPanelId]);

  // Calculate panel position and container height based on full-screen state
  const gapSize = FULL_SCREEN_SPACING.GAP_SIZE;
  const topPadding = gapSize * 2; // Top and bottom padding combined
  const canvasPadding = FULL_SCREEN_SPACING.PANEL_PADDING * 2; // Top + bottom internal padding
  const containerHeight = isFullScreen
    ? `calc(100vh - ${headerHeight}px - ${topPadding}px - ${canvasPadding}px)`
    : `100%`;

  const position = isFullScreen
    ? {
        right: gapSize + PANEL_POSITIONING.FULLSCREEN_RIGHT_OFFSET,
        top:
          headerHeight +
          gapSize +
          PANEL_POSITIONING.FULLSCREEN_TOP_BASE +
          PANEL_POSITIONING.FULLSCREEN_TOP_EXTRA +
          PANEL_POSITIONING.FULLSCREEN_TOP_ADDITIONAL,
      }
    : {
        right: PANEL_POSITIONING.RIGHT_OFFSET,
        top: PANEL_POSITIONING.TOP_OFFSET,
      };

  return (
    <PanelWrapper isFullScreen={isFullScreen}>
      <FloatingPanels
        key={`org-chart-panel-${isFullScreen ? "fullscreen" : "normal"}`}
        panels={panelsToDisplay}
        defaultSelectedPanelId={
          forcePanelId || currentPanelId || defaultPanelId
        }
        containerHeight={containerHeight}
        position={position}
        alignPanels="right"
        eventBus={EventBus}
        zIndex={
          isFullScreen ? Z_INDEX.PANELS : undefined
        } /* Above canvas (z-index: 10001) */
      />
      {isChangelogEnabled && <ChangelogView />}
    </PanelWrapper>
  );
};

OrgChartPanel.propTypes = {
  hideDetailsPanelInitially: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  headerHeight: PropTypes.number,
};

export default OrgChartPanel;
